import * as THREE from 'three';
import {
  FINISH_X,
  HOME_START_X,
  KERB_RADIUS,
  LANE_WIDTH,
  LANES,
  laneOffset,
  mulberry32,
  STRAIGHT,
  TRACK_WIDTH,
} from '@decathlon/shared';
import { box, buildTree, DARK, mat, studTex } from './blocky.js';

export { LANE_WIDTH, LANES, TRACK_WIDTH };
/** The home straight runs along +x; the finish line is at FINISH_X. */
export const STRAIGHT_START = HOME_START_X;
export const STRAIGHT_END = FINISH_X;
const TRACK_RED = '#c1493f';
const LINE = '#f5f5f5';

export function laneZ(lane: number): number {
  return laneOffset(lane);
}

/** A flat ring segment in the XZ plane: angles as in trackPose (x = cx + r sin a, z = cz + r cos a). */
function flatArc(
  cx: number,
  cz: number,
  rInner: number,
  rOuter: number,
  a0: number,
  a1: number,
  colour: string,
  y: number,
  segments = 48,
): THREE.Mesh {
  const pos: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const a = a0 + ((a1 - a0) * i) / segments;
    const s = Math.sin(a);
    const c = Math.cos(a);
    pos.push(cx + rInner * s, y, cz + rInner * c, cx + rOuter * s, y, cz + rOuter * c);
    if (i < segments) {
      const b = i * 2;
      idx.push(b, b + 1, b + 2, b + 1, b + 3, b + 2);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setIndex(idx);
  geo.computeVertexNormals();
  const m = new THREE.Mesh(
    geo,
    new THREE.MeshLambertMaterial({ color: new THREE.Color(colour), side: THREE.DoubleSide }),
  );
  m.receiveShadow = true;
  return m;
}

/**
 * The stadium: a 400 m oval (home straight along +x, anticlockwise laps), infield, a
 * grandstand full of blocky spectators that bounce with the action, and a scoreboard.
 */
export class Stadium {
  readonly group = new THREE.Group();
  private crowd: THREE.InstancedMesh;
  private crowdBase: Float32Array;
  private crowdSeed: Float32Array;
  private dummy = new THREE.Object3D();
  private boardTex: THREE.CanvasTexture;
  private boardCanvas: HTMLCanvasElement;
  private boardLines: string[] = [];
  private time = 0;
  /** 0 murmur .. 1 roar; drives the bounce. */
  excitement = 0;

  constructor() {
    const g = this.group;
    // Baseplate
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(1600, 1600),
      new THREE.MeshLambertMaterial({ map: studTex() }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.02;
    ground.receiveShadow = true;
    g.add(ground);

    // ---- Track surface: two straights and two bends, plus lane lines and kerbs.
    const cx = (HOME_START_X + FINISH_X) / 2;
    const home = box(STRAIGHT, 0.1, TRACK_WIDTH + 0.6, TRACK_RED, cx, 0.0, TRACK_WIDTH / 2, 0.02);
    home.castShadow = false;
    g.add(home);
    const backZ = -2 * KERB_RADIUS - TRACK_WIDTH / 2;
    const back = box(STRAIGHT, 0.1, TRACK_WIDTH + 0.6, TRACK_RED, cx, 0.0, backZ, 0.02);
    back.castShadow = false;
    g.add(back);
    g.add(
      flatArc(
        FINISH_X,
        -KERB_RADIUS,
        KERB_RADIUS - 0.3,
        KERB_RADIUS + TRACK_WIDTH + 0.3,
        0,
        Math.PI,
        TRACK_RED,
        0.05,
      ),
    );
    g.add(
      flatArc(
        HOME_START_X,
        -KERB_RADIUS,
        KERB_RADIUS - 0.3,
        KERB_RADIUS + TRACK_WIDTH + 0.3,
        Math.PI,
        2 * Math.PI,
        TRACK_RED,
        0.05,
      ),
    );
    const lineGeo = new THREE.BoxGeometry(STRAIGHT, 0.02, 0.05);
    for (let i = 0; i <= LANES; i++) {
      const off = i * LANE_WIDTH;
      const l1 = new THREE.Mesh(lineGeo, mat(LINE));
      l1.position.set(cx, 0.06, off);
      const l2 = new THREE.Mesh(lineGeo, mat(LINE));
      l2.position.set(cx, 0.06, -2 * KERB_RADIUS - off);
      g.add(l1, l2);
      const r = KERB_RADIUS + off;
      g.add(flatArc(FINISH_X, -KERB_RADIUS, r - 0.025, r + 0.025, 0, Math.PI, LINE, 0.065, 64));
      g.add(
        flatArc(
          HOME_START_X,
          -KERB_RADIUS,
          r - 0.025,
          r + 0.025,
          Math.PI,
          2 * Math.PI,
          LINE,
          0.065,
          64,
        ),
      );
    }
    // Start line for the 100 m and the finish line.
    const startLine = box(0.08, 0.02, TRACK_WIDTH, LINE, 0, 0.06, TRACK_WIDTH / 2, 0);
    const finishLine = box(0.12, 0.02, TRACK_WIDTH, LINE, FINISH_X, 0.06, TRACK_WIDTH / 2, 0);
    startLine.castShadow = finishLine.castShadow = false;
    g.add(startLine, finishLine);
    // 10 m marks along the near kerb of the home straight.
    for (let m = 10; m < 100; m += 10) {
      const mark = box(0.06, 0.02, 0.4, LINE, m, 0.06, -0.35, 0);
      mark.castShadow = false;
      g.add(mark);
    }
    // Kerb (inside edge) all the way round.
    g.add(box(STRAIGHT, 0.12, 0.15, LINE, cx, 0.06, -0.38, 0.03));
    g.add(box(STRAIGHT, 0.12, 0.15, LINE, cx, 0.06, -2 * KERB_RADIUS + 0.38, 0.03));
    g.add(
      flatArc(FINISH_X, -KERB_RADIUS, KERB_RADIUS - 0.45, KERB_RADIUS - 0.3, 0, Math.PI, LINE, 0.1),
    );
    g.add(
      flatArc(
        HOME_START_X,
        -KERB_RADIUS,
        KERB_RADIUS - 0.45,
        KERB_RADIUS - 0.3,
        Math.PI,
        2 * Math.PI,
        LINE,
        0.1,
      ),
    );
    // Starting blocks for the 100 m in each lane.
    for (let i = 0; i < LANES; i++) {
      g.add(box(0.5, 0.08, 0.1, DARK, -0.6, 0.1, laneZ(i), 0.01));
      g.add(box(0.2, 0.22, 0.28, '#546e7a', -0.55, 0.16, laneZ(i) - 0.12, 0.03));
      g.add(box(0.2, 0.22, 0.28, '#546e7a', -0.9, 0.16, laneZ(i) + 0.12, 0.03));
    }
    // Finish posts with a banner.
    for (const z of [-0.8, TRACK_WIDTH + 0.8])
      g.add(box(0.2, 3.2, 0.2, LINE, FINISH_X, 1.6, z, 0.03));
    g.add(box(0.2, 0.9, TRACK_WIDTH + 1.8, '#1e88e5', FINISH_X, 3.4, TRACK_WIDTH / 2, 0.04));

    // ---- Grandstand along the home straight (+z): stepped rows of seats.
    const standZ0 = TRACK_WIDTH + 3.5;
    const rows = 12;
    const standX0 = HOME_START_X + 4;
    const standLen = STRAIGHT - 8;
    const standCx = standX0 + standLen / 2;
    for (let r = 0; r < rows; r++) {
      const step = box(
        standLen,
        0.8 * (r + 1),
        1.7,
        r % 2 ? '#90a4ae' : '#b0bec5',
        standCx,
        (0.8 * (r + 1)) / 2,
        standZ0 + r * 1.7 + 0.85,
        0.02,
      );
      step.castShadow = false;
      g.add(step);
    }
    g.add(
      box(
        standLen,
        rows * 0.8 + 6,
        0.6,
        '#78909c',
        standCx,
        (rows * 0.8 + 6) / 2,
        standZ0 + rows * 1.7 + 0.3,
        0.05,
      ),
    );
    g.add(
      box(
        standLen + 2,
        0.4,
        rows * 1.7 + 4,
        '#546e7a',
        standCx,
        rows * 0.8 + 6.2,
        standZ0 + (rows * 1.7) / 2 + 0.5,
        0.05,
      ),
    );
    for (let i = 0; i < 8; i++) {
      const x = standX0 + 2 + (i * (standLen - 4)) / 7;
      g.add(
        box(0.35, rows * 0.8 + 6, 0.35, '#37474f', x, (rows * 0.8 + 6) / 2, standZ0 - 0.4, 0.03),
      );
    }
    // A smaller open terrace along the back straight so the far side is not bare.
    const terraceZ0 = -2 * KERB_RADIUS - TRACK_WIDTH - 3.5;
    for (let r = 0; r < 6; r++) {
      const step = box(
        standLen,
        0.8 * (r + 1),
        1.7,
        r % 2 ? '#90a4ae' : '#b0bec5',
        standCx,
        (0.8 * (r + 1)) / 2,
        terraceZ0 - r * 1.7 - 0.85,
        0.02,
      );
      step.castShadow = false;
      g.add(step);
    }

    // ---- Crowd: one instanced mesh of little blocky people, both stands.
    const seatsPerRow = Math.floor(standLen / 0.9);
    const count = seatsPerRow * (rows + 6);
    const person = new THREE.BoxGeometry(0.5, 0.9, 0.45);
    this.crowd = new THREE.InstancedMesh(
      person,
      new THREE.MeshLambertMaterial({ vertexColors: false }),
      count,
    );
    this.crowd.castShadow = false;
    this.crowdBase = new Float32Array(count * 3);
    this.crowdSeed = new Float32Array(count);
    const rng = mulberry32(42);
    const palette = [
      '#e53935',
      '#1e88e5',
      '#fdd835',
      '#43a047',
      '#fb8c00',
      '#8e24aa',
      '#f5f5f5',
      '#00acc1',
      '#ec407a',
      '#5d4037',
    ];
    const col = new THREE.Color();
    let i = 0;
    const seat = (x: number, y: number, z: number) => {
      if (rng() < 0.08) this.crowdBase.set([0, -50, 0], i * 3);
      else this.crowdBase.set([x, y, z], i * 3);
      this.crowdSeed[i] = rng() * Math.PI * 2;
      col.set(palette[Math.floor(rng() * palette.length)]!);
      this.crowd.setColorAt(i, col);
      i++;
    };
    for (let r = 0; r < rows; r++) {
      for (let s = 0; s < seatsPerRow; s++)
        seat(
          standX0 + 0.6 + s * 0.9 + (rng() - 0.5) * 0.2,
          0.8 * (r + 1) + 0.45,
          standZ0 + r * 1.7 + 0.85 + (rng() - 0.5) * 0.3,
        );
    }
    for (let r = 0; r < 6; r++) {
      for (let s = 0; s < seatsPerRow; s++)
        seat(
          standX0 + 0.6 + s * 0.9 + (rng() - 0.5) * 0.2,
          0.8 * (r + 1) + 0.45,
          terraceZ0 - r * 1.7 - 0.85 + (rng() - 0.5) * 0.3,
        );
    }
    this.layoutCrowd(0);
    g.add(this.crowd);

    // ---- Scoreboard on the roof near the finish, facing the track.
    this.boardCanvas = document.createElement('canvas');
    this.boardCanvas.width = 512;
    this.boardCanvas.height = 256;
    this.boardTex = new THREE.CanvasTexture(this.boardCanvas);
    this.boardTex.colorSpace = THREE.SRGBColorSpace;
    const boardY = rows * 0.8 + 6.2 + 3.2;
    const boardZ = standZ0 + 6;
    g.add(box(12.6, 6.6, 0.6, DARK, 96, boardY, boardZ, 0.1));
    const screen = new THREE.Mesh(
      new THREE.PlaneGeometry(12, 6),
      new THREE.MeshBasicMaterial({ map: this.boardTex }),
    );
    screen.position.set(96, boardY, boardZ - 0.31);
    screen.rotation.y = Math.PI;
    g.add(screen);
    g.add(box(0.4, 3.2, 0.4, '#37474f', 91, boardY - 4.8, boardZ, 0.03));
    g.add(box(0.4, 3.2, 0.4, '#37474f', 101, boardY - 4.8, boardZ, 0.03));
    this.setBoard(['DECATHLON', '', 'WELCOME']);

    // ---- Trees outside the oval: beyond both bends and behind the far terrace.
    const trng = mulberry32(7);
    const outer = KERB_RADIUS + TRACK_WIDTH + 6;
    for (let t = 0; t < 46; t++) {
      const tree = buildTree(trng);
      const where = trng();
      if (where < 0.35)
        tree.position.set(
          FINISH_X + outer + 4 + trng() * 40,
          0,
          -KERB_RADIUS + (trng() - 0.5) * 90,
        );
      else if (where < 0.7)
        tree.position.set(
          HOME_START_X - outer - 4 - trng() * 40,
          0,
          -KERB_RADIUS + (trng() - 0.5) * 90,
        );
      else tree.position.set(HOME_START_X + trng() * STRAIGHT, 0, terraceZ0 - 14 - trng() * 30);
      g.add(tree);
    }
  }

  /** Redraw the scoreboard text (up to three lines). */
  setBoard(lines: string[]): void {
    if (lines.join('\n') === this.boardLines.join('\n')) return;
    this.boardLines = lines;
    const ctx = this.boardCanvas.getContext('2d')!;
    ctx.fillStyle = '#0b1118';
    ctx.fillRect(0, 0, 512, 256);
    ctx.fillStyle = '#ffb300';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const sizes = [44, 96, 40];
    const ys = [48, 132, 218];
    lines.forEach((l, i) => {
      ctx.font = `700 ${sizes[i] ?? 40}px "JetBrains Mono", monospace`;
      ctx.fillStyle = i === 1 ? '#ffd54f' : '#ffb300';
      ctx.fillText(l, 256, ys[i] ?? 128);
    });
    this.boardTex.needsUpdate = true;
  }

  private layoutCrowd(t: number): void {
    const n = this.crowd.count;
    const amp = 0.05 + this.excitement * 0.35;
    const rate = 4 + this.excitement * 6;
    for (let i = 0; i < n; i++) {
      const bx = this.crowdBase[i * 3]!;
      const by = this.crowdBase[i * 3 + 1]!;
      const bz = this.crowdBase[i * 3 + 2]!;
      if (by < 0) this.dummy.position.set(bx, by, bz);
      else
        this.dummy.position.set(
          bx,
          by + Math.max(0, Math.sin(t * rate + this.crowdSeed[i]!)) * amp,
          bz,
        );
      this.dummy.updateMatrix();
      this.crowd.setMatrixAt(i, this.dummy.matrix);
    }
    this.crowd.instanceMatrix.needsUpdate = true;
  }

  update(dt: number): void {
    this.time += dt;
    this.layoutCrowd(this.time);
  }
}
